import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '../components/Toaster';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { api, getToken } from '../lib/api';

interface BlocklistRow {
  id: string;
  kind: 'ip' | 'asn' | 'fingerprint' | 'country';
  value: string;
  action: 'block' | 'allow';
  note: string | null;
  createdAt: number;
}

export const Route = createFileRoute('/blocklist')({
  beforeLoad: () => {
    if (!getToken()) throw redirect({ to: '/login' });
  },
  component: BlocklistPage,
});

function BlocklistPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    kind: 'ip' as BlocklistRow['kind'],
    value: '',
    action: 'block' as 'block' | 'allow',
    note: '',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['blocklist'],
    queryFn: () => api<{ rows: BlocklistRow[] }>('/api/blocklist'),
  });

  const add = useMutation({
    mutationFn: () => api('/api/blocklist', { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['blocklist'] });
      setShowAdd(false);
      setForm({ kind: 'ip', value: '', action: 'block', note: '' });
      toast.success('已添加', '规则已生效');
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/blocklist/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['blocklist'] });
      toast.success('已删除', '规则已移除');
    },
  });

  const rows = data?.rows ?? [];
  const KIND_LABELS: Record<string, string> = {
    ip: 'IP',
    asn: 'ASN',
    fingerprint: '指纹',
    country: '国家',
  };

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">黑白名单</h1>
          <p className="mt-1 text-sm text-zinc-500">手动添加 IP/ASN/指纹/国家的封禁或放行规则</p>
        </div>
        <Button onClick={() => setShowAdd(true)}>
          <Plus className="mr-1.5 h-4 w-4" /> 添加规则
        </Button>
      </div>

      {showAdd && (
        <Card className="mb-4 p-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <label htmlFor="bl-kind" className="mb-1 block text-xs font-medium text-zinc-500">
                类型
              </label>
              <select
                id="bl-kind"
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value as BlocklistRow['kind'] })}
                className="w-full rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              >
                <option value="ip">IP 地址</option>
                <option value="asn">ASN 关键词</option>
                <option value="fingerprint">指纹 Hash</option>
                <option value="country">国家代码</option>
              </select>
            </div>
            <div>
              <label htmlFor="bl-value" className="mb-1 block text-xs font-medium text-zinc-500">
                值
              </label>
              <Input
                id="bl-value"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                placeholder={
                  form.kind === 'ip'
                    ? '1.2.3.4'
                    : form.kind === 'asn'
                      ? 'Amazon'
                      : form.kind === 'country'
                        ? 'US'
                        : 'abc123...'
                }
              />
            </div>
            <div>
              <label htmlFor="bl-action" className="mb-1 block text-xs font-medium text-zinc-500">
                动作
              </label>
              <select
                id="bl-action"
                value={form.action}
                onChange={(e) => setForm({ ...form, action: e.target.value as 'block' | 'allow' })}
                className="w-full rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              >
                <option value="block">封禁 (block)</option>
                <option value="allow">放行 (allow)</option>
              </select>
            </div>
            <div>
              <label htmlFor="bl-note" className="mb-1 block text-xs font-medium text-zinc-500">
                备注
              </label>
              <Input
                id="bl-note"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="可选"
              />
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="rounded-md border border-zinc-200 px-3 py-1.5 text-sm dark:border-zinc-700"
            >
              取消
            </button>
            <Button onClick={() => add.mutate()} disabled={!form.value || add.isPending}>
              {add.isPending ? '添加中…' : '添加'}
            </Button>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500 dark:bg-zinc-900/40">
              <tr>
                <th className="px-4 py-2">类型</th>
                <th className="px-4 py-2">值</th>
                <th className="px-4 py-2">动作</th>
                <th className="px-4 py-2">备注</th>
                <th className="px-4 py-2">创建时间</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-zinc-500">
                    加载中…
                  </td>
                </tr>
              )}
              {rows.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-zinc-500">
                    暂无规则
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-zinc-100 dark:border-zinc-800">
                  <td className="px-4 py-2">{KIND_LABELS[r.kind] ?? r.kind}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.value}</td>
                  <td className="px-4 py-2">
                    <Badge variant={r.action === 'block' ? 'destructive' : 'success'}>
                      {r.action === 'block' ? '封禁' : '放行'}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-xs text-zinc-500">{r.note || '-'}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-xs text-zinc-500">
                    {new Date(r.createdAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => remove.mutate(r.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
